import store from './index';
const THREE = window.THREE;
import {
  ForgeSettings,
  GPSSettings,
  ForgeLevel,
  ForgeEquipment,
  ForgeAnnotation,
  ForgeProject,
  ForgeAttachment,
  ForgeInspection,
  ForgeScaffold,
  PointCloudModelInfo,
  PointCloudDownload,
  ForgeModelVersion,
  ForgeCamera,
} from './../scripts/js/forgeModels';
import { getAMISToken } from '../utils/amisAPIs';
import { canRequestAmisToken } from '../utils/amisTokenAccess';
import { $handleError, $has } from '@/utils';
import Vue from 'vue';

export function fillData(data) {
  if (data.selectedModel) {
    store.commit('setSelectedModel', data.selectedModel);
  }

  if (data.settings) {
    data.settings = Object.assign(new ForgeSettings(), data.settings);
    if (data.settings.gpsSettings)
      data.settings.gpsSettings = Object.assign(new GPSSettings(), data.settings.gpsSettings);
    store.state.forgeSettings = data.settings;
    if (data.settings.isInspectionsOn) {
      if (data.settings.isAMIS) {
        if (canRequestAmisToken(data.settings, $has)) {
          getAMISToken()
            .then((token) => {
              if (token) {
                store.commit('SET_AMIS_TOKEN', token);
                store.dispatch('fetchAllAMISTagsDbids', {
                  assetId: store.state.assetId,
                  fetched: true,
                });
                store.dispatch('LOAD_AMIS_UNITS');
              } else {
                $handleError('Failed to connect with A-MIS.');
              }
            })
            .catch(() => {
              $handleError('Failed to connect with A-MIS');
            });
        }
      } else {
        store.dispatch('fetchAllAMISTagsDbids', {
          assetId: store.state.assetId,
          fetched: false,
        });
      }

      store.dispatch('LOAD_AMIS_COLORS');
    }
  }
  if (data.categories && data.categories.length > 0) {
    //to keep the reference to apply on the UI correctly
    store.state.categories = [...data.categories];
  }

  if (data.levels) {
    let levels = ForgeLevel.parseLevelsFromDB(data.levels);

    store.state.selectedLevel = levels.find((o) => o.name == data.selectedLevel);
    store.commit('setLevels', levels);
  }

  if (data.projects) {
    let userSetting = store.state.userSettings.displaySettings.show360;

    let projects = ForgeProject.parseProjectsFromDB(data.projects);

    projects.forEach((p) => {
      p.setAllImagesVisibleInViewers(userSetting);
    });

    store.commit('setProjects', projects);
  }
  if (data.pointCloudDownloads) {
    data.pointCloudDownloads = data.pointCloudDownloads.map((o) =>
      Object.assign(new PointCloudDownload(), o),
    );
    store.commit('setPointCloudDownloads', data.pointCloudDownloads);
  }
  if (data.equipments) {
    let tags = data.equipments.map((o) => Object.assign(new ForgeEquipment(), o));
    tags.forEach((o) => o.parseContent());
    store.commit(
      'setTags',
      tags.filter((o) => !o.tagHarvesterId),
    );
    store.commit(
      'SET_AUTOMATIC_TAGS',
      tags.filter((o) => o.tagHarvesterId),
    );
  }
  if (data.annotations) {
    let annotations = data.annotations.map((o) => Object.assign(new ForgeAnnotation(), o));
    annotations.forEach((o) => o.parseContent());
    store.state.forgeService.sortByName(annotations);
    store.commit('setAnnotations', annotations);
  }
  if (data.inspections?.length) {
    var inspections = data.inspections.map((o) => Object.assign(new ForgeInspection(), o));
    inspections.forEach((o) => o.parseContent());
    store.state.forgeService.sortByName(inspections);
    store.commit('setInspections', inspections);
    store.dispatch('UPDATE_INSPECTIONS_UNITS');
  }
  if (data.scaffolds) {
    let scaffolds = data.scaffolds.map((o) => Object.assign(new ForgeScaffold(), o));
    scaffolds.forEach((o) => {
      //TODO: remove this later
      if (!o.type) o.type = 'scaffold';

      o.position = Object.assign(new THREE.Vector3(), o.position);
      o.scale = Object.assign(new THREE.Vector3(), o.scale);
      o.max = Object.assign(new THREE.Vector3(), o.max);
      o.attachments = ForgeAttachment.parseAttachments(o.attachments);
      o.parseComments();
    });
    store.commit('setScaffolds', scaffolds);
  }

  if (data.models)
    store.state.uploadedModels = data.models
      .filter((m) => m.name !== data.selectedModel)
      .map((o) => Object.assign(new ForgeModelVersion(), o));
  if (data.pointCloudModels) {
    var pointClouds = data.pointCloudModels.map((o) => Object.assign(new PointCloudModelInfo(), o));
    store.state.forgeService.sortByName(pointClouds);
    store.commit('setPointClouds', pointClouds);
  }
  if (data.cameras) {
    let cameras = data.cameras.map((o) => Object.assign(new ForgeCamera(), o));
    store.state.forgeService.sortByName(cameras);
    store.state.cameras = cameras;
  }

  if (data.displayUnit) {
    store.commit('setDisplayUnit', data.displayUnit);
  }

  if (data.selectedModel == 'Empty Model') {
    store.commit('SET_IS_EMPTY_MODEL', true);
  }

  store.state.dataFetched = true;

  if (store.state.forgeService.getMainExtension() && store.state.viewer && store.state.viewer.model)
    store.state.forgeService.getMainExtension().syncTagsToViewer();
}

export function handleAssetInfo(data) {
  if (data && data.selectedModel) {
    store.commit('setSelectedModel', data.selectedModel);
  }

  store.commit('AREAS/SET_DEFAULT_AREA_ID', data.selectedAreaId);
  store.commit('AREAS/SET_SELECTED_AREA_ID', data.selectedAreaId);

  if (data.settings) {
    data.settings = Object.assign(new ForgeSettings(), data.settings);
    if (data.settings.gpsSettings)
      data.settings.gpsSettings = Object.assign(new GPSSettings(), data.settings.gpsSettings);
    store.state.forgeSettings = data.settings;
    if (data.settings.isInspectionsOn) {
      store.dispatch('LOAD_ASSET_UNITS');
      if (data.settings.isAMIS) {
        store.commit('SET_DARK_MODE', true);

        if (canRequestAmisToken(data.settings, $has)) {
          getAMISToken()
            .then((token) => {
              if (token) {
                store.commit('SET_AMIS_TOKEN', token);
                store.dispatch('fetchAllAMISTagsDbids', {
                  assetId: store.state.assetId,
                  fetched: true,
                });
                store.dispatch('LOAD_AMIS_UNITS');
                store.dispatch('LOAD_AMIS_TEMP_PRESS_UNITS');
              } else {
                Vue.prototype.$dialog.notify.warning('Failed to connect with A-MIS.');
              }
            })
            .catch(() => {
              $handleError('Failed to connect with A-MIS');
            });
        }
      } else {
        store.dispatch('fetchAllAMISTagsDbids', {
          assetId: store.state.assetId,
          fetched: false,
        });
      }

      store.dispatch('LOAD_AMIS_COLORS');
    }
  }

  if (data.displayUnit) {
    store.commit('setDisplayUnit', data.displayUnit);
  }

  if (data.selectedModel == 'Empty Model') {
    store.commit('SET_IS_EMPTY_MODEL', true);
  }
}

export function fillModelFromJson(json) {
  fillData(JSON.parse(json));
}
